import { useEffect, useState } from 'react';
import { getSmsLimitBags } from '../services/nuaTalkerCore.service';
import { getCallBlastingLimitBags } from '../services/nuaTalkerNthVoice.service';
import NuaTalkerNthwService from '../services/nuaTalkerNthw.service';
import { managementRequest } from '../services/nuaTalkerNtvaFlow.service';
import { hasVirtualAgentProduct, VIRTUAL_AUDITOR_PRODUCT_CODE } from '../utils/virtualAgentPlan';

export const BAG_METRIC_LABELS = {
  messages: 'Mensajes',
  sms_parts: 'Partes de SMS',
  calls: 'Llamadas',
  templates: 'Plantillas HSM',
  conversations: 'Conversaciones',
  combined_conversations: 'Conversaciones (plantillas + entrantes)',
  custom: 'Personalizada',
  interactions: 'Interacciones',
  minutes: 'Minutos',
  evaluations: 'Evaluaciones',
};

const PRODUCT_LABELS = {
  NTHSSMS: 'SMS',
  NTHSVOI: 'Call Blasting',
  NTHW: 'WhatsApp',
};

/** Agente Virtual usa códigos de consumo propios (NTVA_CHAT / NTVA_VOZ), distintos
 * de los del catálogo de suscripción — ver utils/virtualAgentPlan.js. */
const VIRTUAL_AGENT_PRODUCT_LABELS = {
  NTVA_CHAT: 'Agente Virtual — Chat',
  NTVA_VOZ: 'Agente Virtual — Voz',
  NTVA_TRANSCRIPCION: 'Agente Virtual — Transcripción de audio',
  NTVA_AUDIT: 'Auditores virtuales',
};

/** A diferencia de nth-text/nthvoice, la API de WhatsApp no trae `usagePercent`
 * precalculado — hay que calcularlo acá con lo que sí manda (`usedAmount`/`totalAmount`). */
function withUsagePercent(bag) {
  const total = Number(bag.totalAmount) || 0;
  const used = Number(bag.usedAmount) || 0;
  return { ...bag, usagePercent: total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0 };
}

/**
 * Trae en un solo hook las bolsas de todos los productos con motor de
 * límites que el cliente tenga contratados (hoy: SMS, Call Blasting y
 * WhatsApp). Cada bolsa devuelta trae `productLabel` para saber de cuál
 * producto es. Falla abierto por producto: si uno falla, no tumba a los
 * demás ni deja de mostrar los que sí respondieron.
 */
export default function useAllBags({ clientId, products }) {
  const [loading, setLoading] = useState(true);
  const [bags, setBags] = useState([]);
  const productsKey = (Array.isArray(products) ? products : []).join(',');

  useEffect(() => {
    if (!clientId) return undefined;
    const owns = productsKey ? productsKey.split(',') : [];
    const fetches = [];

    if (owns.includes('NTHSSMS')) {
      fetches.push(
        getSmsLimitBags({ clientId })
          .then((data) =>
            (data?.bags ?? []).map((bag) => ({ ...bag, productLabel: PRODUCT_LABELS.NTHSSMS })),
          )
          .catch(() => []),
      );
    }
    if (owns.includes('NTHSVOI')) {
      fetches.push(
        getCallBlastingLimitBags({ clientId })
          .then((data) =>
            (data?.bags ?? []).map((bag) => ({ ...bag, productLabel: PRODUCT_LABELS.NTHSVOI })),
          )
          .catch(() => []),
      );
    }
    if (owns.includes('NTHW')) {
      fetches.push(
        new NuaTalkerNthwService(clientId)
          .getLimitsUsage()
          .then((data) =>
            (data?.data ?? []).map((bag) => withUsagePercent({ ...bag, productLabel: PRODUCT_LABELS.NTHW })),
          )
          .catch(() => []),
      );
    }

    // `/client_limit` devuelve también las bolsas del auditor (productCode NTVA_AUDIT).
    if (hasVirtualAgentProduct(owns) || owns.includes(VIRTUAL_AUDITOR_PRODUCT_CODE)) {
      fetches.push(
        managementRequest('GET', `/client_limit/${clientId}`)
          .then((data) =>
            (Array.isArray(data) ? data : []).map((bag) =>
              withUsagePercent({
                ...bag,
                metric: bag.limitType,
                productLabel: VIRTUAL_AGENT_PRODUCT_LABELS[bag.productCode] ?? 'Agente Virtual',
              }),
            ),
          )
          .catch(() => []),
      );
    }

    if (fetches.length === 0) {
      setBags([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all(fetches)
      .then((results) => {
        if (cancelled) return;
        setBags(results.flat().filter((bag) => bag.active !== false));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, productsKey]);

  return { loading, bags };
}
